/**
 * Why a project save did not land, as a structured code — and whether trying
 * the same request again has any chance of working.
 *
 * ── The report ──────────────────────────────────────────────────────────────
 * During a build, `POST /api/projects/{id}/files` intermittently answered
 *
 *   { error: "Failed to save files" }   500
 *
 * and the workspace put up `Having trouble saving. Your changes will sync
 * shortly.` Nothing ever synced: `apiFetch` retries connection-level faults
 * only (lib/api.ts, fetchWithNetworkRetry — it deliberately never inspects
 * res.ok), so a 500 was one attempt and done. The change reached the database
 * later only because the NEXT turn re-saved the whole project on top of it, and
 * the toast that said "shortly" had no idea whether that ever happened.
 *
 * So the message was wrong twice over: it promised a retry that did not exist,
 * and it stayed on screen through the save that eventually succeeded.
 *
 * ── Why a code, and not the message ─────────────────────────────────────────
 * `{ error: "Failed to save files" }` is prose. It covers a unique-key race
 * between two concurrent saves (retry lands it), a statement timeout (retry
 * lands it), and a not-null violation (retry never lands it) with one string,
 * so no caller can tell "wait a moment" from "this will never work". That is
 * the same defect lib/publish/github-failure-code.ts was written for, where
 * `msg.includes("409")` disarmed a retry loop for two logged sessions — and the
 * remedy is the same: classify at the boundary, decide on the code.
 *
 * ── This is not language interpretation ─────────────────────────────────────
 * The strings matched here are PostgreSQL SQLSTATEs and PostgREST error codes —
 * machine identifiers from a published spec, not user language. The message
 * regexes exist for one narrow case (PostgREST reports some faults with an
 * empty `code`) and only ever confirm a code we already suspect.
 *
 * Pure: no React, no fetch, no Supabase import. Both sides use it — the route
 * to name the failure, the client to decide whether to retry it.
 */

/**
 * The codes this can produce.
 *
 *   files_save_conflict     another writer got there first. `files` is keyed
 *                           UNIQUE(project_id, file_path, version)
 *                           (001_initial.sql:29) and the route computes
 *                           `version = max + 1` from a SELECT it ran moments
 *                           earlier, so two saves that overlap compute the same
 *                           number and the second one collides. A turn saves
 *                           several times (the edit, up to five autofix rounds,
 *                           the visual editor's own writer at page.tsx) so
 *                           overlapping saves are ordinary, not exotic.
 *                           RETRYABLE: re-reading the versions is the fix.
 *   files_save_timeout      the statement was cancelled for taking too long — a
 *                           big project, a slow replica, a lock held elsewhere.
 *                           RETRYABLE.
 *   files_save_unavailable  the database was unreachable or out of capacity.
 *                           RETRYABLE.
 *   files_save_rejected     the rows themselves are wrong: a null in a NOT NULL
 *                           column, a value past its width, a foreign key with
 *                           no parent, a policy that forbids the write. The
 *                           identical request fails identically forever.
 *                           NOT retryable — and saying so is what lets the UI
 *                           show a real failure immediately instead of spending
 *                           four attempts pretending.
 *   files_save_failed       the tail: something we could not name. Not
 *                           retryable, so an unknown fault can never become an
 *                           unbounded retry loop.
 */
export const SAVE_FAILURE_CODES = [
  "files_save_conflict",
  "files_save_timeout",
  "files_save_unavailable",
  "files_save_rejected",
  "files_save_failed",
] as const;

export type SaveFailureCode = (typeof SAVE_FAILURE_CODES)[number];

export interface ClassifiedSaveFailure {
  code: SaveFailureCode;
  /** May the SAME request be sent again, right now, with a real chance? */
  retryable: boolean;
  /** The underlying text, for the server log and for triage. Never shown. */
  underlying: string;
}

/** SQLSTATEs where the row set is fine and the moment was not. */
const CONFLICT_SQLSTATES: ReadonlySet<string> = new Set([
  "23505", // unique_violation — the version race this module exists for
  "40001", // serialization_failure
  "40P01", // deadlock_detected
  "55P03", // lock_not_available
]);

const TIMEOUT_SQLSTATES: ReadonlySet<string> = new Set([
  "57014", // query_canceled (statement_timeout)
  "57P05", // idle_session_timeout
]);

const UNAVAILABLE_SQLSTATES: ReadonlySet<string> = new Set([
  "08000", // connection_exception
  "08003", // connection_does_not_exist
  "08006", // connection_failure
  "08001", // sqlclient_unable_to_establish_sqlconnection
  "53300", // too_many_connections
  "53400", // configuration_limit_exceeded
  "57P01", // admin_shutdown
  "57P02", // crash_shutdown
  "57P03", // cannot_connect_now
]);

/**
 * SQLSTATEs where the SAME rows will be refused every time. Listed rather than
 * inferred: "not in the retryable sets" is not the same claim, and this one has
 * to be strong enough to justify telling the user it failed for good.
 */
const REJECTED_SQLSTATES: ReadonlySet<string> = new Set([
  "23502", // not_null_violation
  "23503", // foreign_key_violation
  "23514", // check_violation
  "22001", // string_data_right_truncation
  "22P02", // invalid_text_representation
  "42501", // insufficient_privilege (an RLS policy said no)
  "42703", // undefined_column (a migration is not applied)
  "42P01", // undefined_table
]);

/** PostgREST's own codes, which are not SQLSTATEs. */
const POSTGREST_REJECTED: ReadonlySet<string> = new Set([
  "PGRST204", // column not found in the schema cache
  "PGRST205", // table not found in the schema cache
  "PGRST202", // function not found
]);

/**
 * PostgREST occasionally reports a fault with an empty `code`. These two
 * patterns are the only place text is consulted, and only to confirm a class
 * the status/shape already suggests — never to invent one.
 */
const TIMEOUT_TEXT = /statement timeout|canceling statement|query timeout/i;
const UNAVAILABLE_TEXT =
  /connection (refused|reset|closed|terminated)|could not connect|server closed the connection|ECONNRESET|ETIMEDOUT|fetch failed/i;
/** The unique index this module is named for, when the code did not survive. */
const CONFLICT_TEXT =
  /duplicate key value|violates unique constraint|files_project_id_file_path_version_key/i;

/**
 * Classify one failed write against `files`.
 *
 * Takes the error OBJECT (a PostgrestError, or anything thrown) rather than a
 * message, so a route rewording its copy can never silently change what the
 * client does with the failure.
 */
export function classifySaveFailure(error: unknown): ClassifiedSaveFailure {
  const err = (error ?? null) as
    | { code?: string | null; message?: string | null; details?: string | null }
    | null;
  const code = (err?.code ?? "").trim();
  const message =
    (err?.message ?? "").trim() ||
    (error instanceof Error ? error.message : "") ||
    (typeof error === "string" ? error : "");
  const details = (err?.details ?? "").trim();
  const underlying =
    [code && `[${code}]`, message, details].filter(Boolean).join(" ") ||
    "unknown database failure";

  if (CONFLICT_SQLSTATES.has(code)) {
    return { code: "files_save_conflict", retryable: true, underlying };
  }
  if (TIMEOUT_SQLSTATES.has(code)) {
    return { code: "files_save_timeout", retryable: true, underlying };
  }
  if (UNAVAILABLE_SQLSTATES.has(code)) {
    return { code: "files_save_unavailable", retryable: true, underlying };
  }
  if (REJECTED_SQLSTATES.has(code) || POSTGREST_REJECTED.has(code)) {
    return { code: "files_save_rejected", retryable: false, underlying };
  }

  // Code-less fault: the text is all there is. Conflict first — it is the most
  // specific thing this text can say, and the one this module exists for.
  const text = `${message} ${details}`;
  if (CONFLICT_TEXT.test(text)) {
    return { code: "files_save_conflict", retryable: true, underlying };
  }
  if (TIMEOUT_TEXT.test(text)) {
    return { code: "files_save_timeout", retryable: true, underlying };
  }
  if (UNAVAILABLE_TEXT.test(text)) {
    return { code: "files_save_unavailable", retryable: true, underlying };
  }

  return { code: "files_save_failed", retryable: false, underlying };
}

/**
 * Is this failure a UNIQUE-key race the route can settle by itself?
 *
 * Narrower than `retryable`: a timeout is worth another ROUND TRIP but not
 * worth holding an already-slow request open for, whereas a version collision
 * is resolved by re-reading `max(version)` and inserting again — work only the
 * server can do, and work that would otherwise send the user a 500 for a save
 * that was always going to succeed on the next read.
 */
export function isVersionRace(error: unknown): boolean {
  return classifySaveFailure(error).code === "files_save_conflict";
}

/**
 * Gateway statuses that never originate in the route handler — a platform edge
 * or an upstream proxy answered instead. The body carries no code of ours, so
 * status is the only signal, and every one of these means "the request did not
 * reach the code that would have classified it".
 */
const RETRYABLE_STATUSES: ReadonlySet<number> = new Set([502, 503, 504, 429]);

/**
 * Should the CLIENT send this save again, right now?
 *
 * Decides on the structured code the route returns, falling back to the status
 * only for the gateway cases above. A bare 500 with no code is deliberately NOT
 * retried: the route now classifies every failure it produces, so a code-less
 * 500 means the request never got there in a form we can reason about, and
 * three more identical requests would only delay an honest failure.
 */
export function shouldRetrySave(err: {
  code?: string | null;
  status?: number | null;
  retryable?: boolean | null;
}): boolean {
  // The route's own verdict wins — it saw the driver error, we did not.
  if (typeof err.retryable === "boolean") return err.retryable;
  if (err.code && (SAVE_FAILURE_CODES as readonly string[]).includes(err.code)) {
    return (
      err.code === "files_save_conflict" ||
      err.code === "files_save_timeout" ||
      err.code === "files_save_unavailable"
    );
  }
  return typeof err.status === "number" && RETRYABLE_STATUSES.has(err.status);
}
